import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Pagination from '../../components/UI/Pagination';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { getApplications } from '../../services/applications/applicationsService';
import { ApplicationDto } from '../../services/applications/types';
// import { ListApplicationDto } from '../../services/applications/types';

const ApplicationsList: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get the authenticated user from OIDC
  const auth = useAuth();
  
  const pageSize = 10;
  
  const fetchApplications = React.useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view applications.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use the updated application service with OIDC
      // Ensure user is not undefined with null fallback
      const response = await getApplications(auth.user || null, currentPage, pageSize);
      setApplications(response.data || []);
      
      // Use total_pages from response if available
      if (response.total_pages) {
        setTotalPages(response.total_pages);
      } else {
        // Estimate based on the data we have
        // If we received a full page of results, assume there might be more pages
        const receivedFullPage = response.data && response.data.length === pageSize;
        const estimatedTotalPages = receivedFullPage ? 
          Math.max((response.current_page || 1) + 1, 2) : // If full page, at least one more page exists
          (response.current_page || 1); // Otherwise, this might be the last page
        
        setTotalPages(estimatedTotalPages);
      }
    } catch (err) {
      setError('Failed to fetch applications. Please try again.');
      console.error('Error fetching applications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, currentPage, pageSize]);
  
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  return (
    <div>
      <PageHeader
        title="Applications"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications" }
        ]}
      />
      
      <div className="mb-6">
        <Link to="/applications/create">
          <Button variant="primary">
            <i className="fas fa-plus mr-2"></i>
            Add Application
          </Button>
        </Link>
      </div>
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={fetchApplications}
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Identifier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                        No applications found.
                      </td>
                    </tr>
                  ) : (
                    applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <Link 
                            to={`/applications/${app.id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {app.name || 'Unnamed Application'}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {app.identifier || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {app.vendor || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {app.status ? (
                            <StatusBadge status={app.status} />
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {app.created_at ? (
                            new Date(app.created_at).toLocaleDateString()
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Link to={`/applications/${app.id}`}>
                              <Button variant="ghost" size="sm" title="View">
                                <i className="fas fa-eye"></i>
                              </Button>
                            </Link>
                            <Link to={`/applications/${app.id}/edit`}>
                              <Button variant="ghost" size="sm" title="Edit">
                                <i className="fas fa-edit"></i>
                              </Button>
                            </Link>
                            <Link to={`/applications/${app.id}/parameters`}>
                              <Button variant="ghost" size="sm" title="Parameters">
                                <i className="fas fa-cog"></i>
                              </Button>
                            </Link>
                            <Link to={`/applications/${app.id}/users`}>
                              <Button variant="ghost" size="sm" title="Users">
                                <i className="fas fa-users"></i>
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="py-3 px-6 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ApplicationsList;